import { createReducer } from '@reduxjs/toolkit';
import { EntityTypes } from 'constants/entities';
import { IAction, IActionAudio, IActionEntity, IActionTemplate } from 'types/domain/action';
import { IAnimation } from 'types/domain/animation';
import { IAudio, IAudioFile } from 'types/domain/audio';
import { ICertificate } from 'types/domain/certificate';
import { INode, IPortraitVariant, IPosition, IRotation } from 'types/domain/base';
import { IImage, IImageFile } from 'types/domain/image';
import { IMediaAsset, IMediaSystem } from 'types/domain/media';
import { IModel, IModelEntityConfig, IModelFile } from 'types/domain/model';
import { IModule, IModuleOut, IModuleVersion } from 'types/domain/module';
import { ICalendar, IEmail, INotification } from 'types/domain/notifications';
import { IDomain, IOrganisation, IOrganisationConfig, IOrganisationTag, ITheme } from 'types/domain/organisation';
import { IRoleConfig } from 'types/domain/role';
import { IScene, ISceneConfig } from 'types/domain/scene';
import { ITemplate, IText, ITranslation } from 'types/domain/text';
import type { ITemplate as IDesignTemplate } from 'core/services/templates';
import { ITranscription, ITranscriptionItem } from 'types/domain/transcription';
import { IOverride, IVariable, IVariableOption } from 'types/domain/variable';
import {
  IOrgUserVerification,
  IOrgUserVerificationEntity,
  IVerification,
  IVerificationEntity,
  IVerificationRequest,
  IVerificationRequestEntity,
  IVerificationRequestEntityMedia,
} from 'types/domain/verification';
import { IVideo, IVideoFile, IVideoGroup, IVideoTemplate } from 'types/domain/video';
import { IKeyArray } from 'types/IKeyArray';
import { cases } from './cases';
import undoable, { combineFilters } from 'redux-undo';
import { update } from 'core/actions/admin/data';
import {
  ICourseProgress,
  ICertificatePermission,
  ICoursePermission,
  IFolderPermission,
  IGroupAccessPermission,
  IGroupPermission,
  IModulePermission,
  IPermission,
  IStructurePermission,
} from 'types/domain/permissions';
import { ITag, ITagJoin } from 'types/domain/tag';
import { IFolder } from 'types/domain/folder';
import { move } from 'core/actions/admin/diagram';
import {
  ICourse,
  ICourseNode,
  ICourseNodeEntity,
  ICourseNodeGroup,
  ICourseOut,
  ICourseUserVariable,
  ICourseVariable,
} from 'types/domain/course';
import { IForumPost, IForumPostView, IForumTopic } from 'types/domain/forum';
import { IGroup, IGroupUser } from 'types/domain/group';
import { ISearch, ISearchResult } from 'types/domain/search';
import { IService, IServiceAttribute } from 'types/domain/service';
import { ISession, ISessionAttempt, ISessionAction, ISessionResource, ISessionVariable } from 'types/domain/session';
import { IStructure, IStructureGroupInstance, IStructureOut } from 'types/domain/structure';
import {
  IOrganisationSystem,
  IOrganisationSystemValue,
  ISystem,
  ISystemService,
  ISystemAttribute,
  ISystemUser,
} from 'types/domain/system';
import {
  IOrganisationUser,
  IOrgUserCertificate,
  IOrgUserCourse,
  IOrgUserState,
  IOrgUserVariable,
  IOrgUserVariableOverride,
} from 'types/domain/user';
import { IUserImport } from 'types/domain/userImport';
import { IActivity } from 'types/domain/activity';

export interface IEntityReducer {
  [EntityTypes.ACTION]: IKeyArray<IAction>;
  [EntityTypes.ACTION_ENTITY]: IKeyArray<IActionEntity>;
  [EntityTypes.ACTION_AUDIO]: IKeyArray<IActionAudio>;
  [EntityTypes.ACTIVITY]: IKeyArray<IActivity>;
  [EntityTypes.ANIMATION]: IKeyArray<IAnimation>;
  [EntityTypes.AUDIO]: IKeyArray<IAudio>;
  [EntityTypes.AUDIO_FILE]: IKeyArray<IAudioFile>;
  [EntityTypes.CERTIFICATE]: IKeyArray<ICertificate>;
  [EntityTypes.FOLDER]: IKeyArray<IFolder>;
  [EntityTypes.GROUP_ACCESS_PERMISSION]: IKeyArray<IGroupAccessPermission>;
  [EntityTypes.IMAGE]: IKeyArray<IImage>;
  [EntityTypes.IMAGE_FILE]: IKeyArray<IImageFile>;
  [EntityTypes.MEDIA_ASSET]: IKeyArray<IMediaAsset>;
  [EntityTypes.MODEL]: IKeyArray<IModel>;
  [EntityTypes.MODEL_ENTITY_CONFIG]: IKeyArray<IModelEntityConfig>;
  [EntityTypes.MODEL_FILE]: IKeyArray<IModelFile>;
  [EntityTypes.MODULE]: IKeyArray<IModule>;
  [EntityTypes.MODULE_VERSION]: IKeyArray<IModuleVersion>;
  [EntityTypes.MODULE_OUT]: IKeyArray<IModuleOut>;
  [EntityTypes.NODE]: IKeyArray<INode>;
  [EntityTypes.NOTIFICATION]: IKeyArray<INotification>;
  [EntityTypes.ORG_CONFIG]: IKeyArray<IOrganisationConfig>;
  [EntityTypes.ORG_TAG]: IKeyArray<IOrganisationTag>;
  [EntityTypes.PORTRAIT_VARIANT]: IKeyArray<IPortraitVariant>;
  [EntityTypes.POSITION]: IKeyArray<IPosition>;
  [EntityTypes.ROLE_CONFIG]: IKeyArray<IRoleConfig>;
  [EntityTypes.ROTATION]: IKeyArray<IRotation>;
  [EntityTypes.SCENE]: IKeyArray<IScene>;
  [EntityTypes.SCENE_CONFIG]: IKeyArray<ISceneConfig>;
  [EntityTypes.TAG]: IKeyArray<ITag>;
  [EntityTypes.TAG_JOIN]: IKeyArray<ITagJoin>;
  [EntityTypes.THEME]: IKeyArray<ITheme>;
  [EntityTypes.DESIGN_TEMPLATE]: IKeyArray<IDesignTemplate>;
  [EntityTypes.TEXT]: IKeyArray<IText>;
  [EntityTypes.TRANSCRIPTION]: IKeyArray<ITranscription>;
  [EntityTypes.TRANSCRIPTION_ITEM]: IKeyArray<ITranscriptionItem>;
  [EntityTypes.TRANSLATION]: IKeyArray<ITranslation>;
  [EntityTypes.VARIABLE]: IKeyArray<IVariable>;
  [EntityTypes.VARIABLE_OPTION]: IKeyArray<IVariableOption>;
  [EntityTypes.VERIFICATION]: IKeyArray<IVerification>;
  [EntityTypes.VERIFICATION_ENTITY]: IKeyArray<IVerificationEntity>;
  [EntityTypes.VERIFICATION_REQUEST]: IKeyArray<IVerificationRequest>;
  [EntityTypes.VERIFICATION_REQUEST_ENTITY]: IKeyArray<IVerificationRequestEntity>;
  [EntityTypes.VERIFICATION_RESPONSE]: IKeyArray<IVerificationRequestEntityMedia>;
  [EntityTypes.VIDEO]: IKeyArray<IVideo>;
  [EntityTypes.VIDEO_FILE]: IKeyArray<IVideoFile>;
  //admin
  [EntityTypes.ACTION_TEMPLATE]: IKeyArray<IActionTemplate>;
  [EntityTypes.CALENDAR]: IKeyArray<ICalendar>;
  [EntityTypes.CERTIFICATE_PERMISSION]: IKeyArray<ICertificatePermission>;

  [EntityTypes.COURSE]: IKeyArray<ICourse>;
  [EntityTypes.COURSE_NODE]: IKeyArray<ICourseNode>;
  [EntityTypes.COURSE_NODE_ENTITY]: IKeyArray<ICourseNodeEntity>;
  [EntityTypes.COURSE_NODE_GROUP]: IKeyArray<ICourseNodeGroup>;
  [EntityTypes.COURSE_OUT]: IKeyArray<ICourseOut>;
  [EntityTypes.COURSE_PERMISSION]: IKeyArray<ICoursePermission>;
  [EntityTypes.COURSE_PROGRESS]: IKeyArray<ICourseProgress>;
  [EntityTypes.COURSE_VARIABLE]: IKeyArray<ICourseVariable>;
  [EntityTypes.COURSE_USER_VARIABLE]: IKeyArray<ICourseUserVariable>;
  [EntityTypes.DOMAIN]: IKeyArray<IDomain>;
  [EntityTypes.EMAIL]: IKeyArray<IEmail>;
  [EntityTypes.FOLDER_PERMISSION]: IKeyArray<IFolderPermission>;
  [EntityTypes.FORUM_POST]: IKeyArray<IForumPost>;
  [EntityTypes.FORUM_POST_VIEW]: IKeyArray<IForumPostView>;
  [EntityTypes.FORUM_TOPIC]: IKeyArray<IForumTopic>;
  [EntityTypes.GROUP]: IKeyArray<IGroup>;
  [EntityTypes.GROUP_PERMISSION]: IKeyArray<IGroupPermission>;
  [EntityTypes.GROUP_USER]: IKeyArray<IGroupUser>;
  //[EntityTypes.LANG]: IKeyArray<ILanguage>;
  [EntityTypes.MEDIA_SYSTEM]: IKeyArray<IMediaSystem>;
  [EntityTypes.MODULE_PERMISSION]: IKeyArray<IModulePermission>;
  [EntityTypes.ORGANISATION]: IKeyArray<IOrganisation>;
  [EntityTypes.ORG_USER]: IKeyArray<IOrganisationUser>;
  [EntityTypes.ORG_USER_CERTIFICATE]: IKeyArray<IOrgUserCertificate>;
  [EntityTypes.ORG_USER_COURSE]: IKeyArray<IOrgUserCourse>;
  [EntityTypes.ORG_USER_STATE]: IKeyArray<IOrgUserState>;
  [EntityTypes.ORG_USER_VARIABLE]: IKeyArray<IOrgUserVariable>;
  [EntityTypes.ORG_USER_VARIABLE_OVER]: IKeyArray<IOrgUserVariableOverride>;
  [EntityTypes.ORG_USER_VERIFICATION]: IKeyArray<IOrgUserVerification>;
  [EntityTypes.ORG_USER_VERIFICATION_ENTITY]: IKeyArray<IOrgUserVerificationEntity>;
  [EntityTypes.ORG_USER_VERIFICATION_ENTITY_MEDIA]: IKeyArray<IVerificationRequestEntityMedia>;
  [EntityTypes.ORG_SYSTEM]: IKeyArray<IOrganisationSystem>;
  [EntityTypes.ORG_SYSTEM_VALUE]: IKeyArray<IOrganisationSystemValue>;
  [EntityTypes.OVERRIDE]: IKeyArray<IOverride>;
  [EntityTypes.PERMISSION]: IKeyArray<IPermission>;
  [EntityTypes.SEARCH]: IKeyArray<ISearch>;
  [EntityTypes.SEARCH_RESULT]: IKeyArray<ISearchResult>;
  [EntityTypes.SERVICE]: IKeyArray<IService>;
  [EntityTypes.SERVICE_ATTRIBUTE]: IKeyArray<IServiceAttribute>;
  [EntityTypes.SESSION]: IKeyArray<ISession>;
  [EntityTypes.SESSION_ATTEMPT]: IKeyArray<ISessionAttempt>;
  [EntityTypes.SESSION_ACTION]: IKeyArray<ISessionAction>;
  [EntityTypes.SESSION_RESOURCE]: IKeyArray<ISessionResource>;
  [EntityTypes.SESSION_VARIABLE]: IKeyArray<ISessionVariable>;
  [EntityTypes.STRUCTURE]: IKeyArray<IStructure>;
  [EntityTypes.STRUCTURE_GROUP_INSTANCE]: IKeyArray<IStructureGroupInstance>;
  [EntityTypes.STRUCTURE_OUT]: IKeyArray<IStructureOut>;
  [EntityTypes.STRUCTURE_PERMISSION]: IKeyArray<IStructurePermission>;
  [EntityTypes.SYSTEM]: IKeyArray<ISystem>;
  [EntityTypes.SYSTEM_USER]: IKeyArray<ISystemUser>;
  [EntityTypes.SYSTEM_ATTRIBUTE]: IKeyArray<ISystemAttribute>;
  [EntityTypes.SYSTEM_SERVICE]: IKeyArray<ISystemService>;
  [EntityTypes.TEMPLATE]: IKeyArray<ITemplate>;
  [EntityTypes.USER_IMPORT]: IKeyArray<IUserImport>;
  [EntityTypes.VIDEO_GROUP]: IKeyArray<IVideoGroup>;
  [EntityTypes.VIDEO_TEMPLATE]: IKeyArray<IVideoTemplate>;
}

const initialState: IEntityReducer = {
  [EntityTypes.ACTION]: {},
  [EntityTypes.ACTION_ENTITY]: {},
  [EntityTypes.ACTION_TEMPLATE]: {},
  [EntityTypes.ACTION_AUDIO]: {},
  [EntityTypes.ACTIVITY]: {},
  [EntityTypes.ANIMATION]: {},
  [EntityTypes.AUDIO]: {},
  [EntityTypes.AUDIO_FILE]: {},
  [EntityTypes.CERTIFICATE]: {},
  [EntityTypes.DOMAIN]: {},
  [EntityTypes.FOLDER]: {},
  [EntityTypes.GROUP_ACCESS_PERMISSION]: {},
  [EntityTypes.IMAGE]: {},
  [EntityTypes.IMAGE_FILE]: {},
  [EntityTypes.MEDIA_ASSET]: {},
  [EntityTypes.MODEL]: {},
  [EntityTypes.MODEL_ENTITY_CONFIG]: {},
  [EntityTypes.MODEL_FILE]: {},
  [EntityTypes.MODULE]: {},
  [EntityTypes.MODULE_OUT]: {},
  [EntityTypes.MODULE_VERSION]: {},
  [EntityTypes.NODE]: {},
  [EntityTypes.NOTIFICATION]: {},
  [EntityTypes.ORG_CONFIG]: {},
  [EntityTypes.ORG_TAG]: {},
  [EntityTypes.ORG_SYSTEM]: {},
  [EntityTypes.ORG_SYSTEM_VALUE]: {},
  [EntityTypes.PORTRAIT_VARIANT]: {},
  [EntityTypes.POSITION]: {},
  [EntityTypes.ROLE_CONFIG]: {},
  [EntityTypes.ROTATION]: {},
  [EntityTypes.SCENE]: {},
  [EntityTypes.SCENE_CONFIG]: {},
  [EntityTypes.TAG]: {},
  [EntityTypes.TAG_JOIN]: {},
  [EntityTypes.THEME]: {},
  [EntityTypes.DESIGN_TEMPLATE]: {},
  [EntityTypes.TEXT]: {},
  [EntityTypes.TRANSCRIPTION]: {},
  [EntityTypes.TRANSCRIPTION_ITEM]: {},
  [EntityTypes.TRANSLATION]: {},
  [EntityTypes.VARIABLE]: {},
  [EntityTypes.VARIABLE_OPTION]: {},
  [EntityTypes.VERIFICATION]: {},
  [EntityTypes.VERIFICATION_ENTITY]: {},
  [EntityTypes.VERIFICATION_REQUEST]: {},
  [EntityTypes.VERIFICATION_REQUEST_ENTITY]: {},
  [EntityTypes.VERIFICATION_RESPONSE]: {},
  [EntityTypes.VIDEO]: {},
  [EntityTypes.VIDEO_FILE]: {},
  [EntityTypes.CALENDAR]: {},
  [EntityTypes.CERTIFICATE_PERMISSION]: {},
  [EntityTypes.COURSE]: {},
  [EntityTypes.COURSE_OUT]: {},
  [EntityTypes.COURSE_NODE]: {},
  [EntityTypes.COURSE_NODE_ENTITY]: {},
  [EntityTypes.COURSE_NODE_GROUP]: {},
  [EntityTypes.COURSE_PERMISSION]: {},
  [EntityTypes.COURSE_PROGRESS]: {},
  [EntityTypes.COURSE_VARIABLE]: {},
  [EntityTypes.COURSE_USER_VARIABLE]: {},
  [EntityTypes.EMAIL]: {},
  [EntityTypes.FOLDER_PERMISSION]: {},
  [EntityTypes.FORUM_POST]: {},
  [EntityTypes.FORUM_POST_VIEW]: {},
  [EntityTypes.FORUM_TOPIC]: {},
  [EntityTypes.GROUP]: {},
  [EntityTypes.GROUP_PERMISSION]: {},
  [EntityTypes.GROUP_USER]: {},
  //[EntityTypes.LANG]: IKeyArray<ILanguage>;
  [EntityTypes.MEDIA_SYSTEM]: {},
  [EntityTypes.MODULE_PERMISSION]: {},
  [EntityTypes.ORGANISATION]: {},
  [EntityTypes.ORG_USER]: {},
  [EntityTypes.ORG_USER_CERTIFICATE]: {},
  [EntityTypes.ORG_USER_COURSE]: {},
  [EntityTypes.ORG_USER_STATE]: {},
  [EntityTypes.ORG_USER_VARIABLE]: {},
  [EntityTypes.ORG_USER_VARIABLE_OVER]: {},
  [EntityTypes.ORG_USER_VERIFICATION]: {},
  [EntityTypes.ORG_USER_VERIFICATION_ENTITY]: {},
  [EntityTypes.ORG_USER_VERIFICATION_ENTITY_MEDIA]: {},
  [EntityTypes.PERMISSION]: {},
  [EntityTypes.OVERRIDE]: {},
  [EntityTypes.SEARCH]: {},
  [EntityTypes.SEARCH_RESULT]: {},
  [EntityTypes.SERVICE]: {},
  [EntityTypes.SERVICE_ATTRIBUTE]: {},
  [EntityTypes.SESSION]: {},
  [EntityTypes.SESSION_ATTEMPT]: {},
  [EntityTypes.SESSION_ACTION]: {},
  [EntityTypes.SESSION_RESOURCE]: {},
  [EntityTypes.SESSION_VARIABLE]: {},
  [EntityTypes.STRUCTURE]: {},
  [EntityTypes.STRUCTURE_GROUP_INSTANCE]: {},
  [EntityTypes.STRUCTURE_OUT]: {},
  [EntityTypes.STRUCTURE_PERMISSION]: {},
  [EntityTypes.SYSTEM]: {},
  [EntityTypes.SYSTEM_USER]: {},
  [EntityTypes.SYSTEM_ATTRIBUTE]: {},
  [EntityTypes.SYSTEM_SERVICE]: {},
  [EntityTypes.TEMPLATE]: {},
  [EntityTypes.USER_IMPORT]: {},
  [EntityTypes.VIDEO_GROUP]: {},
  [EntityTypes.VIDEO_TEMPLATE]: {},
};

export default undoable(createReducer(initialState, cases), {
  filter: combineFilters(actionFilter, stateFilter),
  syncFilter: true,
  // Each history entry is a copy of the whole entity map, and `update` is filtered in —
  // so on a large module (one entity per transcribed word) an unbounded history grows
  // until the tab runs out of memory. Ten steps is plenty of undo for authoring.
  limit: 10,
});

function actionFilter(action, currentState, previousHistory) {
  switch (action.type) {
    case update.type:
      return true;
    case move.type:
      return true;
    default:
      return false;
  }
}

function stateFilter(action, currentState, previousHistory) {
  return true;
}
